import { useState, useEffect, createContext, useContext } from 'react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CMSContext = createContext();

export const useCMS = () => {
    const context = useContext(CMSContext);
    if (!context) {
        throw new Error('useCMS must be used within a CMSProvider');
    }
    return context;
};

// Default content for fallback
const defaultContent = {
    home: {
        hero: {
            badge: "Search Visibility & AI Discovery Agency",
            headline: "Rank on Google. Dominate Maps. Show up in AI.",
            subheadline: "We don't chase traffic. We capture revenue. Strategic SEO, Maps optimization, and AI search visibility for businesses that want to lead, not follow.",
            cta_primary: "Book Strategy Call",
            cta_secondary: "View Results",
            image: "https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=1200&auto=format&fit=crop&q=80"
        },
        stats: [
            { value: "300+", label: "Clients Served" },
            { value: "10M+", label: "Keywords Ranked" },
            { value: "5x", label: "Avg. Traffic Growth" },
            { value: "98%", label: "Client Retention" }
        ],
        process: {
            title: "How Aitrixy Works",
            subtitle: "A proven 3-step system to transform your search visibility into revenue.",
            steps: [
                { number: "01", title: "Discover", description: "Deep audit of your search presence across Google, Maps, and AI platforms" },
                { number: "02", title: "Rank", description: "Strategic execution to dominate high-intent keywords and local results" },
                { number: "03", title: "Convert", description: "Turn visibility into revenue with optimized user journeys" }
            ]
        },
        why: {
            title: "Not Just Another SEO Agency",
            subtitle: "We're the only agency that combines traditional SEO, Maps optimization, and AI search visibility into one unified strategy.",
            features: [
                { icon: "zap", title: "AI-First Approach", description: "Optimized for ChatGPT, SGE, and emerging AI search" },
                { icon: "map-pin", title: "Maps Mastery", description: "Specialized GMB optimization for local dominance" },
                { icon: "target", title: "Revenue Focus", description: "We measure success in leads and sales, not just traffic" },
                { icon: "trending-up", title: "Proven Results", description: "300+ businesses scaled with our methodology" }
            ],
            stat: { value: "3x", label: "Average ROI in 6 months" }
        }
    },
    about: {
        hero: {
            title: "We Build Search Empires",
            subtitle: "Aitrixy is a next-generation search visibility agency. We help businesses dominate every platform where customers search — from Google and Maps to AI-powered search engines like ChatGPT and Perplexity."
        },
        mission: {
            title: "Our Mission",
            content: "To make every business visible where it matters most. We believe that great products deserve to be found, and we're committed to making that happen through strategic, ethical, and results-driven SEO."
        },
        stats: [
            { value: "300+", label: "Happy Clients" },
            { value: "10M+", label: "Keywords Ranked" },
            { value: "98%", label: "Client Retention" },
            { value: "4+", label: "Years Experience" }
        ]
    },
    settings: {
        company_name: "Aitrixy",
        tagline: "Search Visibility & AI Discovery Agency",
        email: "hello@aitrixy.com",
        phone: "+91 98765 43210",
        whatsapp: "+919876543210",
        address: "New Delhi, India"
    }
};

export const CMSProvider = ({ children }) => {
    const [content, setContent] = useState(defaultContent);
    const [settings, setSettings] = useState(defaultContent.settings);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        fetchContent();
        fetchSettings();
    }, []);

    const fetchContent = async () => {
        try {
            // Fetch all page content in one request
            const pages = ['home', 'about', 'services'];
            const contentPromises = pages.map(page => 
                axios.get(`${API}/content/${page}`).catch(() => ({ data: {} }))
            );
            
            const results = await Promise.all(contentPromises);
            
            const fetchedContent = {};
            pages.forEach((page, index) => {
                if (Object.keys(results[index].data).length > 0) {
                    fetchedContent[page] = results[index].data;
                }
            });

            // Merge with defaults
            setContent(prev => ({
                ...prev,
                ...fetchedContent
            }));
        } catch (error) {
            console.log('Using default content');
        } finally {
            setLoading(false);
        }
    };

    const fetchSettings = async () => {
        try {
            const response = await axios.get(`${API}/settings`);
            if (response.data) {
                setSettings(prev => ({ ...prev, ...response.data }));
            }
        } catch (error) {
            console.log('Using default settings');
        }
    };

    const getContent = (page, section) => {
        return content[page]?.[section] || defaultContent[page]?.[section] || {};
    };

    const updateContent = async (page, section, newContent, token) => {
        try {
            await axios.put(
                `${API}/content/${page}/${section}`,
                { content: newContent },
                { headers: { Authorization: `Bearer ${token}` } }
            );
            
            setContent(prev => ({
                ...prev,
                [page]: {
                    ...prev[page],
                    [section]: newContent
                }
            }));
            
            return true;
        } catch (error) {
            console.error('Error updating content:', error);
            return false;
        }
    };

    const updateSettings = async (newSettings, token) => {
        try {
            await axios.put(
                `${API}/settings`,
                newSettings,
                { headers: { Authorization: `Bearer ${token}` } }
            );
            setSettings(prev => ({ ...prev, ...newSettings }));
            return true;
        } catch (error) {
            console.error('Error updating settings:', error);
            return false;
        }
    };

    return (
        <CMSContext.Provider value={{ 
            content, 
            settings, 
            loading,
            getContent,
            updateContent,
            updateSettings,
            defaultContent
        }}>
            {children}
        </CMSContext.Provider>
    );
};
